import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, DatePicker, Input, InputNumber, Row } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import { dateToDayjs } from '@/utils';
import {
  useGetAdoptionLiaisonsChildQuery,
  useGetCaseWorkersQuery,
  useGetCountiesQuery,
  useGetWWKRecruitersQuery,
} from '@/services/general-api';
import { SaveSvg } from '@/assets/icons';
import Select from '@/components/select';
import Panel from '@/components/panel';
import FixedFooter from '@/components/fixed-footer';
import Button from '@/components/button';
import { Form } from './styles';
import { useSaveGroupMutation } from '@/services/sibling-group-api';

const EditForm = ({ groupData, triggerUpdate }) => {
  const navigate = useNavigate();

  const { data: countryData, isLoading: loadingCountryData } = useGetCountiesQuery();
  const { data: liaisonData, isLoading: loadingLiaisonData } = useGetAdoptionLiaisonsChildQuery();
  const { data: recruiterData, isLoading: loadingRecruiterData } = useGetWWKRecruitersQuery();
  const { data: workerData, isLoading: loadingWorkerData } = useGetCaseWorkersQuery();
  const [saveGroup, { isLoading: saving }] = useSaveGroupMutation();

  const [form] = Form.useForm();

  const initialValues = useMemo(() => {
    if (!groupData) return {};
    const values = _.cloneDeep(groupData);
    values.referral = dateToDayjs(values.referral);
    return values;
  }, [groupData]);

  const onFinish = async (body) => {
    body.referral = body.referral.toISOString();

    try {
      const res = await saveGroup({ groupId: groupData?.siblingGroupId, body }).unwrap();
      if (groupData) {
        triggerUpdate();
        navigate(`/sibling-group/${res.siblingGroupId}/view`);
      } else {
        navigate(`/sibling-group/${res.siblingGroupId}/add-child`);
      }
    } catch (err) {}
  };

  return (
    <Form layout="vertical" form={form} initialValues={initialValues} onFinish={onFinish}>
      <Panel style={{ marginBottom: 58 }}>
        <Row gutter={32}>
          <Col xs={24} md={12} xl={8}>
            <Form.Item name="referral" label="Initial Referral Date" rules={[{ required: true }]}>
              <DatePicker
                format="MM/DD/YYYY"
                onBlur={(e) => {
                  const inputVal = e.target.value;
                  const validDate = moment(inputVal, ['M/D/YYYY', 'MM/DD/YYYY'], true);
                  if (validDate.isValid()) {
                    form.setFieldsValue({
                      referral: validDate,
                    });
                  } else {
                    form.setFieldsValue({
                      referral: null,
                    });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={8} style={{ display: 'flex', gap: 16 }}>
            <Form.Item name={['county', 'id']} label="County" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Select
                options={countryData}
                loading={loadingCountryData}
                showSearch
                filterOption={(input, option) => option.name.toLowerCase().startsWith(input.toLowerCase())}
                onChange={(id) => {
                  const personId = _.find(countryData, { id }).adoptionLiaison.personId;

                  const personExists = liaisonData.some((option) => option.id === personId);

                  if (personExists) {
                    form.setFieldValue(['adoptionLiaison', 'personId'], personId);
                  } else {
                    form.setFieldValue(['adoptionLiaison', 'personId'], undefined);
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.county?.id !== currentValues.county?.id}
            >
              {({ getFieldValue }) => {
                const id = getFieldValue('county')?.id;
                const country = _.find(countryData, { id });
                return (
                  <Form.Item label=" " style={{ width: 70 }}>
                    {country?.region.name}
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Form.Item name={['adoptionLiaison', 'personId']} label="Adoption Liaison" rules={[{ required: true }]}>
              <Select options={liaisonData} loading={loadingLiaisonData} showSearch />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Form.Item name={['caseworker', 'personId']} label="Caseworker" rules={[{ required: true }]}>
              <Select options={workerData} loading={loadingWorkerData} showSearch />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={8} style={{ display: 'none' }}>
            <Form.Item name={['wwkRecruiter', 'personId']} label="WWK Recruiter">
              <Select options={recruiterData} loading={loadingRecruiterData} allowClear showSearch />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Form.Item name={['magikEmail', 'emailAddress']} label="MaGIK Email" rules={[{ type: 'email' }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={12} xl={8}>
            <Form.Item name="entrySiblings" label="Number of Siblings" rules={[{ required: true }]}>
              <InputNumber min={2} max={10} />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item name="bio" label="Bio" rules={[{ required: true }]}>
              <Input.TextArea autoSize={{ minRows: 3 }} />
            </Form.Item>
          </Col>
        </Row>
      </Panel>

      <FixedFooter>
        <Button type="primary" htmlType="submit" icon={<SaveSvg />} loading={saving}>
          Save
        </Button>
      </FixedFooter>
    </Form>
  );
};

export default EditForm;
